import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from '@tanstack/react-router';
import { AdsValueIssue, } from '@wit-support/admin-contracts';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { InlineNotice, TextLink, formatCount } from '@wit-support/ui';
import { Info, TriangleAlert } from 'lucide-react';
import { getAsset, getBatchRepeatInput, getLaunch } from '../api.js';
import { AdsTime } from './ads-time.js';
import { sourceItemOfAsset } from './media-picker-dialog.js';
import { spendTotalText } from './spend-summary.js';
import { postItemOf, mediaItemOf } from './creative-items.js';
const FROM_RE = /^(batch|launch):([a-f0-9]{24})$/;
/** `?from=` → что повторять; не по форме — `'invalid'`; нет — null. */
export function parseRepeatFrom(raw) {
    if (raw == null || raw === '')
        return null;
    if (typeof raw !== 'string')
        return 'invalid';
    const m = FROM_RE.exec(raw);
    return m ? { kind: m[1], id: m[2] } : 'invalid';
}
/** Пачка или запуск не найдены (404 / неверный id / запуск вне пачки). */
export function isRepeatNotFound(err) {
    return (err instanceof ModuleRequestError &&
        (err.status === 404 || err.code === 'invalid_id' || err.upstreamCode === 'launch-not-in-batch'));
}
/**
 * Загрузка повтора: у запуска — сначала его пачка; файлы медиатеки — для
 * карточек креативов (сбой чтения файла не мешает: карточка без превью).
 */
export async function loadRepeat(from) {
    let batchId = from.id;
    let launchId = null;
    if (from.kind === 'launch') {
        const l = await getLaunch(from.id);
        if (l.batchId == null)
            throw new ModuleRequestError('not_found', 404);
        batchId = l.batchId;
        launchId = from.id;
    }
    const repeat = await getBatchRepeatInput(batchId, launchId);
    const ids = (repeat.input?.items ?? []).flatMap((i) => i.kind === 'media' && i.origin == null ? [i.assetId] : []);
    const got = await Promise.allSettled([...new Set(ids)].map((id) => getAsset(id)));
    const files = {};
    for (const r of got) {
        if (r.status === 'fulfilled')
            files[r.value.assetId] = sourceItemOfAsset(r.value);
    }
    return { repeat, files };
}
/** Креативы входа пачки → карточки формы (тексты, кнопка, ссылка — как были). */
export function repeatUiItems(items, files) {
    return items.map((i) => i.kind === 'post'
        ? postItemOf(i.objectStoryId)
        : mediaItemOf(i, files[i.assetId] ?? fallbackFile(i)));
}
/** Файл без чтения медиатеки (файл Meta с `origin` или не прочитался): имя из `origin`. */
function fallbackFile(i) {
    return {
        source: i.origin ? 'in-ads' : 'uploaded',
        assetId: i.assetId,
        origin: i.origin
            ? {
                platformAccountId: i.origin.platformAccountId,
                ...(i.origin.imageHash ? { imageHash: i.origin.imageHash } : {}),
                ...(i.origin.videoId ? { videoId: i.origin.videoId } : {}),
                ...(i.origin.name ? { name: i.origin.name } : {}),
            }
            : null,
        kind: i.origin?.videoId ? 'video' : 'image',
        name: i.origin?.name ?? 'Файл из прошлой пачки',
        previewUrl: null,
        width: null,
        height: null,
        durationSec: null,
        inAccounts: [],
        creative: null,
        usage: null,
        createdAt: null,
    };
}
/**
 * Старт прошёл (`start-reset`) — замечание к полю «Старт»: поле пустое и
 * подсвечено, пропадает, как только старт выбран заново.
 */
export function repeatFieldIssues(notes) {
    return notes
        .filter((n) => n.code === 'start-reset')
        .map((n) => AdsValueIssue.parse({
        fieldKey: 'schedule.start',
        code: n.code,
        severity: 'warning',
        message: n.message,
    }));
}
/** Шаг формы, куда ведёт заметка (§11.6 «пункты ведут к шагу»). */
const NOTE_STEP = {
    'account-dropped': 2,
    'no-accounts': 2,
    'post-missing': 3,
    'post-unchecked': 3,
    'media-missing': 3,
    'media-unchecked': 3,
    'no-items': 3,
    'launch-items-unknown': 3,
    'override-dropped': 4,
    'override-dropped-unknown': 4,
    'bid-from-launch': 4,
    'group-dropped': 5,
    'group-new': 5,
    'group-edit-dropped': 5,
    'groups-unavailable': 5,
    'start-reset': 6,
    'funnel-override-dropped': 7,
};
/** Заметки, у которых своё место в плашке (деньги, шаблон), — не в общем списке. */
const OWN_PLACE = new Set([
    'spend-changed',
    'spend-not-compared',
    'template-changed',
    'groups-appeared',
]);
function noteLine(n) {
    const step = NOTE_STEP[n.code];
    return step ? `${n.message} (шаг ${step})` : n.message;
}
/** «(сегодня)», «(вчера)», «(5 дней назад)», «(месяц назад)» — возраст пачки. */
export function batchAgeText(iso, now) {
    const days = Math.floor((now - Date.parse(iso)) / 86_400_000);
    if (!Number.isFinite(days) || days < 1)
        return 'сегодня';
    if (days === 1)
        return 'вчера';
    if (days < 30)
        return `${formatCount(days, ['день', 'дня', 'дней'])} назад`;
    const months = Math.floor(days / 30);
    return months === 1
        ? 'месяц назад'
        : `${formatCount(months, ['месяц', 'месяца', 'месяцев'])} назад`;
}
/** «$481.11» (или по строке на валюту) — итог «станет» сервера; неизвестен — null. */
function afterTotal(r) {
    const after = r.spend.after ?? [];
    if (after.length === 0 || after.some((l) => l.usable === false))
        return null;
    return after.map((l) => spendTotalText(l)).join(' + ');
}
/**
 * Первая строка плашки — деньги (§11.6, ревью SB-5 P1-2):
 * - `spend-changed` — «⚠ Деньги изменились: было $80.00 в день → станет
 *   $160.00 (до уникальных центов) — причина» (суммы — готовые строки сервера
 *   `params.beforeRu/afterRu/reasonsRu`, AA-5);
 * - `spend-not-compared` или у прошлой пачки нет сумм (`spend.before` пуст) —
 *   «⚠ Не удалось сравнить деньги с прошлой пачкой — проверьте сумму в день»
 *   (+ «станет» сервера), без «как в прошлый раз»;
 * - иначе — «Деньги как в прошлый раз: $80.00 в день».
 */
export function repeatMoneyLine(r) {
    const changed = r.notes.find((n) => n.code === 'spend-changed');
    const will = afterTotal(r);
    if (changed) {
        const p = changed.params ?? {};
        const str = (k) => (typeof p[k] === 'string' && p[k] !== '' ? p[k] : null);
        const before = str('beforeRu');
        const after = str('afterRu');
        const reasons = str('reasonsRu');
        if (before && after) {
            return {
                warn: true,
                text: `⚠ Деньги изменились: было ${before} в день → станет ${after} (до уникальных центов)${reasons ? ` — ${reasons}` : ''}.`,
            };
        }
        return { warn: true, text: `⚠ ${changed.message}` };
    }
    const notCompared = r.notes.some((n) => n.code === 'spend-not-compared') || r.spend.before.length === 0;
    if (notCompared) {
        return {
            warn: true,
            text: `⚠ Не удалось сравнить деньги с прошлой пачкой — проверьте сумму в день${will ? `: станет ${will}` : ''}.`,
        };
    }
    if (will == null)
        return { warn: false, text: 'Сумму в день покажет «Проверить».' };
    // сумма — на момент заполнения по пачке; после правок формы итог — в футере
    return {
        warn: false,
        text: `При заполнении по пачке: ${will} в день — как в прошлый раз.`,
    };
}
/**
 * Плашка над шагом 1 (§11.6) — строится по ответу сервера: откуда
 * заполнено, деньги первой строкой, режим, «Не перенеслось», «Шаблон
 * изменили после этой пачки» с полями «было → стало».
 */
export function RepeatBanner({ repeat: r, now = Date.now(), }) {
    const money = repeatMoneyLine(r);
    const changed = r.notes.find((n) => n.code === 'template-changed');
    const appeared = r.notes.find((n) => n.code === 'groups-appeared');
    const rest = r.notes.filter((n) => !OWN_PLACE.has(n.code));
    const lost = rest.filter((n) => n.severity !== 'info');
    const info = rest.filter((n) => n.severity === 'info');
    const more = changed?.params?.more;
    return (_jsx(InlineNotice, { variant: "info", bordered: true, icon: Info, as: "div", "data-testid": "repeat-banner", children: _jsxs("div", { className: "flex min-w-0 flex-col gap-2 [overflow-wrap:anywhere]", children: [_jsxs("span", { children: ["\u0417\u0430\u043F\u043E\u043B\u043D\u0435\u043D\u043E \u043F\u043E ", r.source.launchId ? 'запуску пачки' : 'пачке', " \u043E\u0442", ' ', _jsx(AdsTime, { iso: r.source.createdAt, preset: "dateTimeNumeric" }), " (", batchAgeText(r.source.createdAt, now), "), \u0448\u0430\u0431\u043B\u043E\u043D \u00AB", r.template?.name ?? r.source.templateName, "\u00BB."] }), _jsxs("span", { className: money.warn ? 'font-medium text-[var(--color-state-warning-fg)]' : 'font-medium', "data-testid": "repeat-money", children: [money.warn ? (_jsx(TriangleAlert, { "aria-hidden": true, className: "mr-1 inline h-4 w-4 align-text-bottom" })) : null, money.text.replace(/^⚠ /, '')] }), appeared ? (
                // перенеслось (все группы отмечены) — не в «Не перенеслось», а «проверьте»
                _jsxs("span", { className: "font-medium text-[var(--color-state-warning-fg)]", "data-testid": "repeat-groups-appeared", children: [appeared.message, " (\u0448\u0430\u0433 5)"] })) : null, (r.input?.mode ?? r.source.mode) === 'draft' ? (_jsx("span", { children: "\u0412 \u043F\u0440\u043E\u0448\u043B\u044B\u0439 \u0440\u0430\u0437 \u043F\u0430\u0447\u043A\u0430 \u0431\u044B\u043B\u0430 \u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u043E\u043C \u2014 \u00AB\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u00BB \u043E\u0442\u043C\u0435\u0447\u0435\u043D, \u043A\u0430\u043A \u0442\u043E\u0433\u0434\u0430 (\u0432\u043D\u0438\u0437\u0443 \u0444\u043E\u0440\u043C\u044B)." })) : null, info.length > 0 ? (_jsx("ul", { className: "flex flex-col gap-0.5", "data-testid": "repeat-info", children: info.map((n, i) => (_jsx("li", { children: noteLine(n) }, `${n.code}-${i}`))) })) : null, _jsxs("span", { children: ["\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0438 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C\u00BB.", ' ', _jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/fb-launches/batches/$id", params: { id: r.source.batchId }, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0438\u0441\u0445\u043E\u0434\u043D\u0443\u044E \u043F\u0430\u0447\u043A\u0443" }) })] }), lost.length > 0 ? (_jsxs("div", { "data-testid": "repeat-lost", children: [_jsx("span", { className: "font-medium", children: "\u041D\u0435 \u043F\u0435\u0440\u0435\u043D\u0435\u0441\u043B\u043E\u0441\u044C:" }), _jsx("ul", { className: "ml-4 list-disc", children: lost.map((n, i) => (_jsx("li", { children: noteLine(n) }, `${n.code}-${i}`))) })] })) : null, changed ? (_jsxs("div", { "data-testid": "repeat-template-changed", children: [_jsxs("span", { className: "font-medium", children: [changed.message, ":"] }), _jsxs("ul", { className: "ml-4 list-disc", children: [(changed.changedFields ?? []).map((f, i) => (_jsxs("li", { children: [f.label, ": \u0431\u044B\u043B\u043E ", f.before, ", \u0441\u0442\u0430\u043B\u043E ", f.after] }, `${f.label}-${i}`))), typeof more === 'number' && more > 0 ? _jsxs("li", { children: ["\u0438 \u0435\u0449\u0451 ", more] }) : null] })] })) : null] }) }));
}
//# sourceMappingURL=repeat.js.map